<template>
  <SoContainer>
    <h4 class="mb-4">Tariffs</h4>
    <SoAlert
      v-if="showVatChangeBanner"
      type="info"
      title="From 1 October, electricity VAT temporarily drops from 5% to 0%."
      data-testid="electricity-vat-change-banner"
      class="mb-4"
    >
      Prices shown reflect the lower rate, but until 1 October you'll still pay
      5% on electricity.
    </SoAlert>
    <div class="d-flex gap-1">
      <SoCheckbox v-model="tariffQuoter.isDirectDebit" label="Direct Debit" />
      <SoCheckbox v-model="tariffQuoter.includeNSC" label="Inc. NSC" />
      <SoCheckbox
        v-model="tariffQuoter.showTariffBreakdown"
        :label="
          tariffQuoter.quoteForMeterpoints
            ? 'Show Per Meterpoint'
            : 'Show Per Fuel'
        "
      />
    </div>
    <SoRow v-if="!tariffQuoter.filteredBundles.length">
      <SoCol :default-width="6">
        <SoAlert>No Tariffs Found</SoAlert>
      </SoCol>
    </SoRow>
    <template v-else>
      <SoRadioCards v-model="tariffQuoter.selectedBundle">
        <SoRow>
          <SoCol
            v-for="bundle in tariffQuoter.filteredBundles"
            :key="bundle.code"
            :default-width="3"
            :lg-and-down-width="4"
            :mobile-width="4"
          >
            <BundleRadioCard :bundle="bundle" data-testid="tq-bundle-card" />
          </SoCol>
        </SoRow>
      </SoRadioCards>

      <template
        v-if="
          tariffQuoter.showTariffBreakdown && !tariffQuoter.quoteForMeterpoints
        "
      >
        <template v-for="fuel in tariffQuoter.selectedFuels" :key="fuel">
          <div class="mt-8 d-flex align-end">
            <SoIcon
              :icon="fuel === 'electricity' ? 'electric' : 'gas'"
              :color="fuel === 'electricity' ? 'caution' : 'neutral'"
              size="x-large"
            />
            <h4 class="text-capitalize">{{ fuel }}</h4>
          </div>
          <div class="ml-2 mb-4 text-disabled">
            Usage:
            <template v-if="fuel === 'electricity'">
              {{
                tariffQuoter.fuelUsage.electricity
                  ? `${tariffQuoter.fuelUsage.electricity} kWh`
                  : `(Day) ${tariffQuoter.fuelUsage.electricityDay} kWh (Night) ${tariffQuoter.fuelUsage.electricityNight} kWh`
              }}
            </template>
            <template v-else>
              {{ `${tariffQuoter.fuelUsage.gas} kWh` }}
            </template>
          </div>
          <SoRadioCards
            v-model="
              tariffQuoter.selectedTariffsFuel[fuel === 'electricity' ? 0 : 1]
            "
          >
            <TariffRadioCards
              :fuel-type="fuel"
              data-testid="tq-fuel-tariff-card"
            />
          </SoRadioCards>
        </template>
      </template>
      <template
        v-else-if="
          tariffQuoter.showTariffBreakdown && tariffQuoter.quoteForMeterpoints
        "
      >
        <template
          v-for="(mpxn, index) in tariffQuoter.meterpointFuelUsage"
          :key="mpxn.id"
        >
          <div class="mt-8 mb-2 d-flex gap-1 align-center">
            <SoAvatar
              :type="mpxn.isElectricity ? 'electric' : 'gas'"
              class="pb-2"
            />
            <div>
              <h5 class="text-capitalize" style="line-height: 12px !important">
                {{ mpxn.isElectricity ? "MPAN" : "MPRN" }} - {{ mpxn.id }}
              </h5>
              <div class="text-disabled">
                Usage:
                <template v-if="mpxn.isElectricity">
                  {{
                    !mpxn.isTwoRate
                      ? `${mpxn.usage.electricity} kWh`
                      : `(Day) ${mpxn.usage.electricityDay} kWh (Night) ${mpxn.usage.electricityNight} kWh`
                  }}
                </template>
                <template v-else>
                  {{ `${mpxn.usage.gas} kWh` }}
                </template>
              </div>
            </div>
          </div>
          <SoRadioCards v-model="tariffQuoter.selectedTariffsMeterpoint[index]">
            <TariffRadioCards
              :fuel-type="mpxn.isElectricity ? 'electricity' : 'gas'"
              :usage="getMPXNUsage(mpxn)"
              data-testid="tq-mpxn-tariff-card"
            />
          </SoRadioCards>
        </template>
      </template>
    </template>
  </SoContainer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useTariffQuoter, IMPXNUsage } from "@/stores/tariffQuoter"
  import { IUsageEstimation } from "@/services/UsageEstimationService/types"
  import BundleRadioCard from "../components/BundleRadioCard.vue"
  import TariffRadioCards from "../components/TariffRadioCards.vue"
  import SoContainer from "@/components/SoContainer.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoAvatar from "@/components/SoAvatar.vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"

  export default defineComponent({
    components: {
      BundleRadioCard,
      TariffRadioCards,
      SoContainer,
      SoRow,
      SoCol,
      SoRadioCards,
      SoAlert,
      SoCheckbox,
      SoIcon,
      SoAvatar,
    },
    data() {
      return {
        tariffQuoter: useTariffQuoter(),
      }
    },
    computed: {
      showVatChangeBanner() {
        return (
          FeaturesService.isFeatureActive("SHOW_ELEC_VAT_RATE_CHANGE_INFO") ===
          true
        )
      },
      selectedBundle() {
        return this.tariffQuoter.selectedBundle
      },
      showTariffBreakdown() {
        return this.tariffQuoter.showTariffBreakdown
      },
    },
    watch: {
      selectedBundle(val) {
        this.tariffQuoter.setSelectedTariffs(val)
      },
      showTariffBreakdown() {
        this.tariffQuoter.setSelectedTariffs()
      },
    },
    methods: {
      getMPXNUsage(mpxn: IMPXNUsage): Partial<IUsageEstimation> {
        if (mpxn.isElectricity) {
          return mpxn.isTwoRate
            ? {
                electricityDay: mpxn.usage.electricityDay,
                electricityNight: mpxn.usage.electricityNight,
              }
            : { electricity: mpxn.usage.electricity }
        } else {
          return { gas: mpxn.usage.gas }
        }
      },
    },
  })
</script>
